<template>
    <div ref="scrollList" class="sc-conversation-list">
        <div
            v-if="!loading && conversations.length === 0"
            class="sc-conversation-list__empty has-text-centered"
        >
            <e-icon icon="comments-alt" size="is-large" class="sc-conversation-list__empty-icon" />
            <p class="sc-conversation-list__empty-title">No Conversations Yet</p>
            <p class="sc-conversation-list__empty-subtitle">
                Start a new conversation by hitting the button below.
            </p>
        </div>
        <aside v-else class="menu">
            <ul class="menu-list tw:w-full">
                <li v-for="conversation in conversations" :key="conversation.id">
                    <a
                        class="is-flex is-align-items-center"
                        :class="{
                            'is-active': conversation.id === currentConversation,
                        }"
                        :data-conversation-id="conversation.id"
                        @click="switchConversation(conversation.id)"
                    >
                        <user-status
                            :status="conversationStatus(conversation)"
                            :current="conversation.id === currentConversation"
                        />
                        <user-avatar
                            :img-url="conversationAvatarUrl(conversation)"
                            :title="conversationTitle(conversation)"
                            size="32x32"
                            class="sc-conversation-list__avatar m-r-sm"
                        />
                        <span class="sc-conversation-list__title">
                            {{ conversationTitle(conversation) }}
                        </span>
                        <span v-if="conversation.unread" class="is-new-message-count m-l-auto">
                            {{ conversation.unread }}
                        </span>
                    </a>
                </li>
            </ul>
        </aside>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import Avatar from '@/components/elements/Avatar.vue';
import { DELETED_CHAT_USER } from '@/constants/chat.js';
import UserStatus from './UserStatus.vue';

const { mapState } = createNamespacedHelpers('Chat');

export default {
    name: 'ChatConversations',

    components: { UserAvatar: Avatar, UserStatus },

    emits: ['loading', 'switch-window'],

    props: {
        colors: {
            type: Object,
            required: true,
        },

        conversations: {
            type: Array,
            default: () => [],
        },

        onConversationSwitch: {
            type: Function,
            required: true,
        },

        currentConversation: {
            type: Number,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        online: {
            type: Object,
            default: () => {},
        },
    },

    computed: {
        ...mapState(['authUser']),
    },

    watch: {
        currentConversation() {
            this.scrollToCurrentConversation();
        },

        conversations: {
            handler() {
                this.scrollToCurrentConversation();
            },
            deep: true,
        },
    },

    mounted() {
        this.scrollToCurrentConversation();
    },

    methods: {
        scrollToCurrentConversation() {
            this.$nextTick(() => {
                const list = this.$refs.scrollList;

                if (!list || !this.currentConversation) {
                    return;
                }

                const activeItem = list.querySelector(`[data-conversation-id="${this.currentConversation}"]`);

                if (!activeItem) {
                    return;
                }

                if (this.conversations[0]?.id === this.currentConversation) {
                    list.scrollTop = 0;

                    return;
                }

                activeItem.scrollIntoView({ block: 'nearest' });
            });
        },

        switchConversation(id) {
            this.$emit('loading', true);
            this.$emit('switch-window', 'messages');

            const switching = this.onConversationSwitch(id);

            switching.then(() => this.$emit('loading', false));
        },

        conversationStatus(conversation) {
            return (
                conversation.participants
                    .filter(
                        participant =>
                            participant.messageable_type === 'App\\Models\\User' &&
                            Number(participant.messageable_id) !== this.authUser.id
                    )
                    // @todo Potential issue if we introduce more messageable types. messageable is polymorphic, but here we're only checking the id and not the type
                    .some(participant => !!this.online[participant.messageable_id])
            );
        },

        conversationTitle({ participants }) {
            if (!participants.length) {
                return '';
            }

            const others = participants.filter(participant => Number(participant.messageable_id) !== this.authUser.id);

            // A participant whose account was deleted arrives with no messageable.
            const name = participant => (participant.messageable ?? DELETED_CHAT_USER).name;

            if (others.length > 1) {
                return `You, ${name(others[0])} & others`;
            }
            return others[0] ? name(others[0]) || '' : '';
        },

        conversationAvatarUrl({ participants }) {
            if (!participants || !participants.length) {
                return null;
            }

            const other = participants.find(
                participant =>
                    participant.messageable_type === 'App\\Models\\User' &&
                    Number(participant.messageable_id) !== this.authUser.id
            );

            if (other && !other.messageable) {
                return DELETED_CHAT_USER.photo_url;
            }

            return other?.messageable?.photo_url ?? null;
        },

        conversationColor(conversation) {
            if (conversation.id !== this.currentConversation) {
                return {};
            }

            return {
                backgroundColor: this.colors.activeConversation.bg,
                color: this.colors.activeConversation.text,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-conversation-list {
    height: 100%;
    overflow-y: auto;
    background-size: 100%;
    padding: 0;

    &__empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        height: 100%;
        padding: $size-4;
    }

    &__empty-icon {
        color: $grey-muted;
        margin-bottom: $size-5;
        font-size: 3rem;
    }

    &__empty-title {
        font-weight: $weight-bold;
        color: $grey-text;
    }

    &__empty-subtitle {
        font-weight: $weight-normal;
        color: $grey-text;
        font-size: $size-7;
    }

    &__avatar {
        flex-shrink: 0;
    }

    &__title {
        min-width: 0;
    }

    ul {
        li {
            a {
                border-radius: 0;
                display: flex;
                padding: $size-9 $column-gap;

                &.is-active {
                    background: $success;
                    color: $success-invert;
                }
            }
        }
    }
}
</style>
